import { Flex } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaButton, RanaLoading } from "@nens/ranalib";

import { ProcessRunChildProps } from "@/features/project-process-run";
import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";
import { ProcessRunFormMapViewStateProvider } from "@/stores/process-run-form-map-view-state-provider";
import { JobStatusOrCreating } from "@/types/job-status-or-creating";

import { useProcessRunFormCollection } from "./hooks/use-process-run-form-collection";
import { ProcessRunForm } from "./process-run-form";

export interface ProcessRunFormsProps extends ProcessRunChildProps {
  status: JobStatusOrCreating;
  setIsCreating: (isCreating: boolean) => void;
}

/**
 * Renders all RJSF-based forms for a single process run: the name form, the input
 * parameters form, and the output parameters form. It is also responsible for the
 * submit/copy-run action buttons.
 *
 * ProcessRunFormMapViewStateProvider is introduced to store a state that is shared
 * through all the map components for a single process run
 */
export function ProcessRunFormCollection(props: ProcessRunFormsProps) {
  const { status, process, job, changeCurrentJobToSourceJob } = props;
  const {
    isLoading,
    nameSchema,
    nameFormData,
    setNameFormData,
    inputSchema,
    setInputFormData,
    outputSchema,
    setOutputFormData,
    errors,
    formIsValid,
    startProcess,
    outputFormData,
    inputFormData,
  } = useProcessRunFormCollection(props);
  const { canWriteProject } = useProjectActionAllowed();
  const intl = useIntl();
  const processId = process.id;

  if (isLoading) return <RanaLoading isCentered />;
  return (
    <ProcessRunFormMapViewStateProvider>
      <ProcessRunForm
        formData={nameFormData}
        setFormData={setNameFormData}
        schema={nameSchema}
        status={status}
        processId={processId}
      />
      <ProcessRunForm
        title={intl.formatMessage({
          id: "process.input",
          defaultMessage: "Input",
        })}
        formData={inputFormData}
        setFormData={setInputFormData}
        schema={inputSchema}
        errors={errors?.inputs}
        status={status}
        processId={processId}
      />
      <ProcessRunForm
        title={intl.formatMessage({
          id: "process.output",
          defaultMessage: "Output",
        })}
        formData={outputFormData}
        setFormData={setOutputFormData}
        schema={outputSchema}
        status={status}
        errors={errors?.outputs}
        isOutput
        processId={processId}
      />

      <Flex mt={11} justifyContent="flex-end">
        {canWriteProject && (
          <>
            {job && (
              <RanaButton onClick={changeCurrentJobToSourceJob} variant="primary">
                <FormattedMessage id="process.copy_process_run" defaultMessage="Copy process run" />
              </RanaButton>
            )}
            {!job && (
              <RanaButton
                isDisabled={!formIsValid}
                title={
                  !formIsValid
                    ? intl.formatMessage({
                        id: "submit.not_all_required_fields_filled",
                        defaultMessage:
                          "Not all required fields are filled or something else is invalid.",
                      })
                    : ""
                }
                variant="primary"
                onClick={startProcess}
              >
                <FormattedMessage id="process.run_process" defaultMessage="Run process" />
              </RanaButton>
            )}
          </>
        )}
      </Flex>
    </ProcessRunFormMapViewStateProvider>
  );
}
